/**
 * Password policy matching your Cognito User Pool configuration.
 * Keep these values in sync with your Cognito User Pool's password policy
 * (AWS Console → Cognito → User Pools → Your Pool → Sign-in experience → Password policy).
 *
 * Policy options:
 * - minLength: 6-99
 * - requireUppercase, requireLowercase, requireNumbers
 * - requireSymbolsOrSpace: special character or space
 * - noLeadingTrailingSpace: must not start or end with space
 */
import { getCognitoConfig } from './index'

const DEFAULT_POLICY = {
  minLength: 8,
  requireUppercase: true,
  requireLowercase: true,
  requireNumbers: true,
  requireSymbolsOrSpace: true,
  noLeadingTrailingSpace: true
}

const SPECIAL_OR_SPACE_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/? ]/

export const getPasswordPolicy = () => {
  const cognitoConfig = getCognitoConfig()
  return { ...DEFAULT_POLICY, ...cognitoConfig.passwordPolicy }
}

/**
 * Validator function for password fields. Returns true if valid.
 */
export const validatePassword = (value) => {
  if (!value || typeof value !== 'string') return false
  const policy = getPasswordPolicy()
  if (value.length < policy.minLength) return false
  if (policy.requireUppercase && !/[A-Z]/.test(value)) return false
  if (policy.requireLowercase && !/[a-z]/.test(value)) return false
  if (policy.requireNumbers && !/\d/.test(value)) return false
  if (policy.requireSymbolsOrSpace && !SPECIAL_OR_SPACE_REGEX.test(value)) return false
  if (policy.noLeadingTrailingSpace && value !== value.trim()) return false
  return true
}

/**
 * Get validation message for invalid password (for wiskInput validations).
 */
export const getPasswordValidationMessage = (translations) => {
  const policy = getPasswordPolicy()
  const parts = []
  const minMsg = translations?.txtAuthPassMinLength
  parts.push(minMsg ? minMsg.replace('{n}', policy.minLength) : `At least ${policy.minLength} characters`)
  if (policy.requireUppercase) parts.push(translations?.txtAuthPassRequireUppercase || 'one uppercase letter')
  if (policy.requireLowercase) parts.push(translations?.txtAuthPassRequireLowercase || 'one lowercase letter')
  if (policy.requireNumbers) parts.push(translations?.txtAuthPassRequireNumbers || 'one number')
  if (policy.requireSymbolsOrSpace) parts.push(translations?.txtAuthPassRequireSymbolOrSpace || 'one special character or space')
  if (policy.noLeadingTrailingSpace) parts.push(translations?.txtAuthPassNoLeadingTrailingSpace || 'no leading or trailing spaces')
  return 'Password must have: ' + parts.join(', ')
}

/**
 * Get helper text describing password requirements (for display under the field).
 */
export const getPasswordHelperText = (translations) => {
  const policy = getPasswordPolicy()
  const parts = []
  const minMsg = translations?.txtAuthPassMinLength
  parts.push(minMsg ? minMsg.replace('{n}', policy.minLength) : `${policy.minLength}+ characters`)
  if (policy.requireUppercase) parts.push(translations?.txtAuthPassRequireUppercase || 'uppercase')
  if (policy.requireLowercase) parts.push(translations?.txtAuthPassRequireLowercase || 'lowercase')
  if (policy.requireNumbers) parts.push(translations?.txtAuthPassRequireNumbers || 'number')
  if (policy.requireSymbolsOrSpace) parts.push(translations?.txtAuthPassRequireSymbolOrSpace || 'special char or space')
  if (policy.noLeadingTrailingSpace) parts.push(translations?.txtAuthPassNoLeadingTrailingSpace || 'no leading/trailing spaces')
  return '(' + parts.join(', ') + ')'
}

/**
 * Get wiskInput validation object for password field.
 */
export const getPasswordValidation = () => ({
  type: 'password',
  validator: validatePassword,
  // message: getPasswordValidationMessage(translations)
  message: ''
})

/**
 * Get list of password rules with labels and pass-check for checklist UI.
 * Each item: { id, label, pass(value) }.
 */
export const getPasswordRules = translations => {
  const policy = getPasswordPolicy()
  const rules = []
  const minMsg = translations?.txtAuthPassMinLength
  rules.push({
    id: 'minLength',
    label: minMsg ? minMsg.replace('{n}', policy.minLength) : `At least ${policy.minLength} characters`,
    pass: v => !!v && v.length >= policy.minLength
  })
  if (policy.requireUppercase) {
    rules.push({
      id: 'uppercase',
      label: translations?.txtAuthPassRequireUppercase || 'One uppercase letter',
      pass: v => !!v && /[A-Z]/.test(v)
    })
  }
  if (policy.requireLowercase) {
    rules.push({
      id: 'lowercase',
      label: translations?.txtAuthPassRequireLowercase || 'One lowercase letter',
      pass: v => !!v && /[a-z]/.test(v)
    })
  }
  if (policy.requireNumbers) {
    rules.push({
      id: 'numbers',
      label: translations?.txtAuthPassRequireNumbers || 'One number',
      pass: v => !!v && /\d/.test(v)
    })
  }
  if (policy.requireSymbolsOrSpace) {
    rules.push({
      id: 'symbols',
      label: translations?.txtAuthPassRequireSymbolOrSpace || 'One special character or space',
      pass: v => !!v && SPECIAL_OR_SPACE_REGEX.test(v)
    })
  }
  if (policy.noLeadingTrailingSpace) {
    rules.push({
      id: 'noSpaces',
      label: translations?.txtAuthPassNoLeadingTrailingSpace || 'No leading or trailing spaces',
      pass: v => typeof v === 'string' && v === v.trim()
    })
  }
  return rules
}
